import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../services/api';

interface UserProfile {
  id: number;
  name: string;
  phone: string;
  email: string;
  bio: string | null;
  avatar: string | null;
  account_type: string;
  status: string;
  is_verified: boolean;
  city: { id: number; name_ar: string } | null;
  rating_avg: string;
  rating_count: number;
  ads_count: number;
  badges: { type: string; label: string }[];
  last_seen_at: string | null;
  created_at: string;
}

interface Ad {
  id: number;
  title: string;
  price: number;
  city: { name_ar: string } | null;
  category: { name_ar: string } | null;
  images: { image_url: string; thumbnail_url: string | null }[];
  views_count: number;
  created_at: string;
  published_at: string;
}

interface Review {
  id: number;
  rating: number;
  comment: string | null;
  role: string;
  reviewer: { id: number; name: string; avatar: string | null } | null;
  created_at: string;
}

const UserProfilePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [user, setUser] = useState<UserProfile | null>(null);
  const [ads, setAds] = useState<Ad[]>([]);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [activeTab, setActiveTab] = useState<'ads' | 'reviews'>('ads');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        setLoading(true);
        const [userRes, adsRes, reviewsRes] = await Promise.all([
          api.get('/users/' + id),
          api.get('/users/' + id + '/ads'),
          api.get('/users/' + id + '/reviews'),
        ]);
        setUser(userRes.data.data);
        const adsData = adsRes.data.data;
        setAds(adsData?.ads || adsData?.data || adsData || []);
        setReviews(reviewsRes.data.data || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
    window.scrollTo(0, 0);
  }, [id]);

  const timeAgo = (dateStr: string) => {
    const diff = Math.floor((new Date().getTime() - new Date(dateStr).getTime()) / 1000);
    if (diff < 60) return 'الآن';
    if (diff < 3600) return 'منذ ' + Math.floor(diff / 60) + ' دقيقة';
    if (diff < 86400) return 'منذ ' + Math.floor(diff / 3600) + ' ساعة';
    if (diff < 2592000) return 'منذ ' + Math.floor(diff / 86400) + ' يوم';
    return 'منذ ' + Math.floor(diff / 2592000) + ' شهر';
  };

  const memberSince = (dateStr: string) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const months = ['يناير','فبراير','مارس','أبريل','مايو','يونيو','يوليو','أغسطس','سبتمبر','أكتوبر','نوفمبر','ديسمبر'];
    return months[d.getMonth()] + ' ' + d.getFullYear();
  };

  const renderStars = (rating: number) => {
    return [1, 2, 3, 4, 5].map(s => (
      <span key={s} style={{ color: s <= rating ? '#FF9800' : '#E0E0E0', fontSize: 14 }}>★</span>
    ));
  };

  if (loading) {
    return (
      <div className="up-wrap">
        <style>{styles}</style>
        <div className="up-skeleton">
          <div className="up-skel-banner"></div>
          <div className="up-skel-body">
            <div className="up-skel-circle"></div>
            <div className="up-skel-line w60"></div>
            <div className="up-skel-line w40"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="up-wrap">
        <style>{styles}</style>
        <div className="up-empty-page">
          <span style={{ fontSize: 48 }}>👤</span>
          <p>المستخدم غير موجود</p>
          <Link to="/ads" className="up-back-link">العودة للإعلانات</Link>
        </div>
      </div>
    );
  }

  const ratingNum = parseFloat(user.rating_avg) || 0;
  const isCompany = user.account_type === 'company';

  return (
    <>
      <style>{styles}</style>
      <div className="up-wrap">
        {/* Banner + Avatar */}
        <div className="up-card">
          <div className="up-banner">
            <div className="up-banner-pattern"></div>
          </div>
          <div className="up-profile-section">
            <div className="up-avatar-area">
              <div className="up-avatar">
                {user.avatar ? (
                  <img src={user.avatar} alt={user.name} />
                ) : (
                  <span>{user.name.charAt(0)}</span>
                )}
                {user.is_verified && <div className="up-verified-badge">✓</div>}
              </div>
            </div>

            <div className="up-info">
              <div className="up-name-row">
                <h1 className="up-name">{user.name}</h1>
                <div className="up-badges">
                  {isCompany && <span className="up-badge up-badge-company">🏢 متجر</span>}
                  {!isCompany && <span className="up-badge up-badge-individual">👤 فرد</span>}
                  {user.is_verified && <span className="up-badge up-badge-verified">✓ موثّق</span>}
                  {user.status === 'active' && <span className="up-badge up-badge-active">● نشط</span>}
                </div>
              </div>

              {user.bio && <p className="up-bio">{user.bio}</p>}

              <div className="up-meta-row">
                {user.city && <span className="up-meta-item">📍 {user.city.name_ar}</span>}
                <span className="up-meta-item">📅 عضو منذ {memberSince(user.created_at)}</span>
                {user.last_seen_at && <span className="up-meta-item">🟢 آخر ظهور {timeAgo(user.last_seen_at)}</span>}
              </div>
            </div>

            <div className="up-actions">
              <button className="up-btn-primary">💬 محادثة</button>
              <button className="up-btn-secondary">+ متابعة</button>
            </div>
          </div>

          {/* Stats */}
          <div className="up-stats">
            <div className="up-stat">
              <div className="up-stat-value">{user.ads_count}</div>
              <div className="up-stat-label">إعلان</div>
            </div>
            <div className="up-stat">
              <div className="up-stat-value up-stat-rating">
                {renderStars(Math.round(ratingNum))}
                <span className="up-rating-num">{ratingNum.toFixed(1)}</span>
              </div>
              <div className="up-stat-label">{user.rating_count} تقييم</div>
            </div>
            <div className="up-stat">
              <div className="up-stat-value">{user.badges?.length || 0}</div>
              <div className="up-stat-label">شارة</div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="up-tabs">
          <button className={'up-tab' + (activeTab === 'ads' ? ' active' : '')} onClick={() => setActiveTab('ads')}>
            الإعلانات ({Array.isArray(ads) ? ads.length : 0})
          </button>
          <button className={'up-tab' + (activeTab === 'reviews' ? ' active' : '')} onClick={() => setActiveTab('reviews')}>
            التقييمات ({Array.isArray(reviews) ? reviews.length : 0})
          </button>
        </div>

        {/* Content */}
        {activeTab === 'ads' ? (
          Array.isArray(ads) && ads.length > 0 ? (
            <div className="up-ads-grid">
              {ads.map((ad: Ad) => (
                <Link key={ad.id} to={'/ads/' + ad.id} className="up-ad-card">
                  <div className="up-ad-img">
                    {ad.images && ad.images.length > 0 ? (
                      <img src={ad.images[0].thumbnail_url || ad.images[0].image_url} alt={ad.title} />
                    ) : (
                      <div className="up-ad-img-empty">📷</div>
                    )}
                  </div>
                  <div className="up-ad-body">
                    <div className="up-ad-title">{ad.title}</div>
                    <div className="up-ad-meta">
                      {ad.city && <span>📍 {ad.city.name_ar}</span>}
                      <span>👁 {ad.views_count || 0}</span>
                      <span>🕐 {timeAgo(ad.published_at || ad.created_at)}</span>
                    </div>
                    <div className="up-ad-price">{Number(ad.price).toLocaleString()} <small>ر.س</small></div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="up-empty">
              <span style={{ fontSize: 36 }}>📋</span>
              <p>لا توجد إعلانات</p>
            </div>
          )
        ) : (
          Array.isArray(reviews) && reviews.length > 0 ? (
            <div className="up-reviews-list">
              {reviews.map((review: Review) => (
                <div key={review.id} className="up-review-card">
                  <div className="up-review-header">
                    <div className="up-review-avatar">
                      {review.reviewer?.avatar ? (
                        <img src={review.reviewer.avatar} alt="" />
                      ) : (
                        <span>{review.reviewer?.name?.charAt(0) || '؟'}</span>
                      )}
                    </div>
                    <div className="up-review-info">
                      <div className="up-review-name">{review.reviewer?.name || 'مستخدم'}</div>
                      <div className="up-review-stars">{renderStars(review.rating)}</div>
                    </div>
                    <div className="up-review-time">{timeAgo(review.created_at)}</div>
                  </div>
                  {review.comment && <p className="up-review-comment">{review.comment}</p>}
                  <span className="up-review-role">{review.role === 'buyer' ? 'مشتري' : 'بائع'}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="up-empty">
              <span style={{ fontSize: 36 }}>⭐</span>
              <p>لا توجد تقييمات</p>
            </div>
          )
        )}
      </div>
    </>
  );
};

const styles = `
  .up-wrap{max-width:900px;margin:0 auto;padding:20px;font-family:'Cairo',sans-serif}
  .up-card{background:#fff;border-radius:12px;border:1px solid #E0E0E0;overflow:hidden;margin-bottom:20px}
  .up-banner{height:120px;background:linear-gradient(135deg,#1976D2,#0D47A1);position:relative;overflow:hidden}
  .up-banner-pattern{position:absolute;inset:0;background:url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")}
  .up-profile-section{padding:0 30px 20px;display:flex;align-items:flex-start;gap:20px;flex-wrap:wrap}
  .up-avatar-area{margin-top:-40px;flex-shrink:0}
  .up-avatar{width:88px;height:88px;border-radius:50%;background:#1976D2;display:flex;align-items:center;justify-content:center;font-size:32px;font-weight:700;color:#fff;overflow:hidden;border:4px solid #fff;box-shadow:0 2px 10px rgba(0,0,0,0.15);position:relative}
  .up-avatar img{width:100%;height:100%;object-fit:cover}
  .up-verified-badge{position:absolute;bottom:2px;right:2px;width:22px;height:22px;background:#4CAF50;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:12px;color:#fff;border:2px solid #fff}
  .up-info{flex:1;min-width:200px;padding-top:12px}
  .up-name-row{display:flex;align-items:center;gap:10px;flex-wrap:wrap;margin-bottom:6px}
  .up-name{font-size:20px;font-weight:700;color:#212121;margin:0}
  .up-badges{display:flex;gap:6px;flex-wrap:wrap}
  .up-badge{padding:3px 10px;border-radius:9999px;font-size:11px;font-weight:600}
  .up-badge-company{background:#E3F2FD;color:#1976D2}
  .up-badge-individual{background:#F5F5F5;color:#757575}
  .up-badge-verified{background:#E8F5E9;color:#4CAF50}
  .up-badge-active{background:#E8F5E9;color:#4CAF50}
  .up-bio{font-size:14px;color:#616161;line-height:1.7;margin-bottom:8px}
  .up-meta-row{display:flex;gap:16px;flex-wrap:wrap}
  .up-meta-item{font-size:12px;color:#9E9E9E;display:flex;align-items:center;gap:4px}
  .up-actions{display:flex;gap:8px;padding-top:12px;flex-shrink:0}
  .up-btn-primary{background:#1976D2;color:#fff;border:none;padding:8px 20px;border-radius:8px;font-family:'Cairo',sans-serif;font-size:13px;font-weight:600;cursor:pointer;transition:background 0.2s}
  .up-btn-primary:hover{background:#0D47A1}
  .up-btn-secondary{background:#fff;color:#1976D2;border:1.5px solid #1976D2;padding:8px 20px;border-radius:8px;font-family:'Cairo',sans-serif;font-size:13px;font-weight:600;cursor:pointer;transition:all 0.2s}
  .up-btn-secondary:hover{background:#E3F2FD}
  .up-stats{display:grid;grid-template-columns:repeat(3,1fr);border-top:1px solid #EEEEEE}
  .up-stat{padding:16px;text-align:center;border-left:1px solid #EEEEEE}
  .up-stat:last-child{border-left:none}
  .up-stat-value{font-size:16px;font-weight:700;color:#212121;margin-bottom:2px;font-family:'Inter','Cairo',sans-serif}
  .up-stat-rating{display:flex;align-items:center;justify-content:center;gap:4px}
  .up-rating-num{font-size:14px;color:#424242;font-weight:600}
  .up-stat-label{font-size:11px;color:#9E9E9E;font-weight:500}

  /* Tabs */
  .up-tabs{display:flex;gap:0;margin-bottom:16px;border-bottom:2px solid #E0E0E0}
  .up-tab{padding:10px 24px;font-size:14px;font-weight:600;color:#757575;background:none;border:none;border-bottom:3px solid transparent;cursor:pointer;font-family:'Cairo',sans-serif;margin-bottom:-2px;transition:all 0.2s}
  .up-tab:hover{color:#424242}
  .up-tab.active{color:#1976D2;border-bottom-color:#1976D2}

  /* Ads Grid */
  .up-ads-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:12px;margin-bottom:20px}
  .up-ad-card{background:#fff;border-radius:10px;border:1px solid #E0E0E0;overflow:hidden;text-decoration:none;color:inherit;transition:all 0.2s}
  .up-ad-card:hover{box-shadow:0 4px 12px rgba(0,0,0,0.07);transform:translateY(-1px)}
  .up-ad-img{height:140px;background:#F5F5F5;overflow:hidden}
  .up-ad-img img{width:100%;height:100%;object-fit:cover}
  .up-ad-img-empty{width:100%;height:100%;display:flex;align-items:center;justify-content:center;font-size:32px;background:linear-gradient(135deg,#E3F2FD,#BBDEFB);color:#1976D2}
  .up-ad-body{padding:12px 14px}
  .up-ad-title{font-size:14px;font-weight:600;color:#212121;margin-bottom:6px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
  .up-ad-meta{display:flex;gap:12px;font-size:11px;color:#9E9E9E;margin-bottom:8px;flex-wrap:wrap}
  .up-ad-price{font-size:16px;font-weight:700;color:#1976D2;font-family:'Inter','Cairo',sans-serif}
  .up-ad-price small{font-size:11px;font-weight:500;color:#757575;font-family:'Cairo',sans-serif}

  /* Reviews */
  .up-reviews-list{display:flex;flex-direction:column;gap:10px;margin-bottom:20px}
  .up-review-card{background:#fff;border-radius:10px;border:1px solid #E0E0E0;padding:16px 20px}
  .up-review-header{display:flex;align-items:center;gap:12px;margin-bottom:10px}
  .up-review-avatar{width:36px;height:36px;border-radius:50%;background:#E0E0E0;display:flex;align-items:center;justify-content:center;font-size:14px;font-weight:700;color:#757575;overflow:hidden;flex-shrink:0}
  .up-review-avatar img{width:100%;height:100%;object-fit:cover}
  .up-review-info{flex:1}
  .up-review-name{font-size:13px;font-weight:600;color:#212121}
  .up-review-stars{display:flex;gap:1px}
  .up-review-time{font-size:11px;color:#BDBDBD}
  .up-review-comment{font-size:13px;color:#616161;line-height:1.7;margin-bottom:8px}
  .up-review-role{font-size:10px;color:#9E9E9E;background:#F5F5F5;padding:2px 8px;border-radius:9999px}

  /* Empty & Loading */
  .up-empty{background:#fff;border-radius:10px;border:1px solid #E0E0E0;padding:50px 20px;text-align:center;margin-bottom:20px}
  .up-empty p{color:#9E9E9E;font-size:14px;margin:8px 0 0}
  .up-empty-page{text-align:center;padding:80px 20px}
  .up-empty-page p{color:#9E9E9E;font-size:16px;margin:12px 0}
  .up-back-link{color:#1976D2;font-size:13px;text-decoration:none}
  .up-skeleton{background:#fff;border-radius:12px;border:1px solid #E0E0E0;overflow:hidden}
  .up-skel-banner{height:120px;background:linear-gradient(90deg,#F5F5F5 25%,#EEEEEE 50%,#F5F5F5 75%);background-size:200%;animation:shimmer 1.5s infinite}
  .up-skel-body{padding:30px}
  .up-skel-circle{width:80px;height:80px;border-radius:50%;background:#F5F5F5;margin:-60px 0 16px}
  .up-skel-line{height:16px;background:#F5F5F5;border-radius:6px;margin-bottom:10px}
  .up-skel-line.w60{width:60%}
  .up-skel-line.w40{width:40%}
  @keyframes shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}

  @media(max-width:640px){
    .up-profile-section{padding:0 16px 16px;flex-direction:column;align-items:center;text-align:center}
    .up-name-row{justify-content:center}
    .up-meta-row{justify-content:center}
    .up-actions{justify-content:center}
    .up-ads-grid{grid-template-columns:1fr}
    .up-avatar-area{margin-top:-44px}
  }
`;

export default UserProfilePage;
